/**
 * @typedef {Object} AIModelConfig
 * @property {string} id
 * @property {string} litellmId
 * @property {string} label
 * @property {'Flagship'|'Fast'|'Economical'} badge
 * @property {string} description
 * @property {number} contextWindow
 * @property {number} maxOutputTokens
 * @property {'anthropic'|'openai'} provider
 */

/** @type {AIModelConfig[]} Mirrors backend/model_registry.py */
export const ANTHROPIC_MODELS = [
  { id: 'claude-sonnet-4-6', litellmId: 'anthropic/claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Consigliato)', badge: 'Flagship', description: 'Ottimo equilibrio tra qualità del codice e costo', contextWindow: 200000, maxOutputTokens: 64000, isDefault: true, provider: 'anthropic' },
  { id: 'claude-opus-5-5', litellmId: 'anthropic/claude-opus-5-5', label: 'Claude Opus 5.5 (Massima qualità)', badge: 'Flagship', description: 'Ragionamento profondo per giochi complessi; costo più alto', contextWindow: 200000, maxOutputTokens: 64000, provider: 'anthropic' },
  { id: 'claude-sonnet-5-5', litellmId: 'anthropic/claude-sonnet-5-5', label: 'Claude Sonnet 5.5 (Più recente)', badge: 'Flagship', description: "Ultima generazione Sonnet, ideale per l'uso quotidiano", contextWindow: 200000, maxOutputTokens: 64000, provider: 'anthropic' },
  { id: 'claude-haiku-5-5', litellmId: 'anthropic/claude-haiku-5-5', label: 'Claude Haiku 5.5 (Veloce ed economico)', badge: 'Fast', description: 'Risposte rapide per modifiche semplici e test', contextWindow: 200000, maxOutputTokens: 64000, provider: 'anthropic' },
  { id: 'claude-fable-5-1', litellmId: 'anthropic/claude-fable-5-1', label: 'Claude Fable 5.1 (Avanzato)', badge: 'Flagship', description: 'Per progetti molto avanzati; più lento e costoso', contextWindow: 200000, maxOutputTokens: 64000, provider: 'anthropic' },
];

/** @type {AIModelConfig[]} */
export const OPENAI_MODELS = [
  { id: 'gpt-4o', litellmId: 'openai/gpt-4o', label: 'GPT-4o (Multimodale / Ragionamento avanzato)', badge: 'Flagship', description: 'Modello OpenAI generalista', contextWindow: 128000, maxOutputTokens: 16000, provider: 'openai' },
  { id: 'gpt-4o-mini', litellmId: 'openai/gpt-4o-mini', label: 'GPT-4o Mini (Ultra-rapido / Basso consumo)', badge: 'Economical', description: 'Economico e veloce', contextWindow: 128000, maxOutputTokens: 16000, provider: 'openai' },
];

export const BYOK_PROVIDERS = {
  anthropic: { provider: 'anthropic', keyPrefix: 'sk-ant-', defaultModelId: 'claude-sonnet-4-6', models: ANTHROPIC_MODELS },
  openai: { provider: 'openai', keyPrefix: 'sk-', defaultModelId: 'gpt-4o', models: OPENAI_MODELS },
};

export const BYOK_MODELS = [...ANTHROPIC_MODELS, ...OPENAI_MODELS];
export const DEFAULT_BYOK_MODEL = BYOK_PROVIDERS.anthropic.defaultModelId;

/** @type {Record<string, string>} Old saved IDs -> active replacement */
export const DEPRECATED_MODEL_FALLBACKS = {
  'claude-3-5-sonnet-20241022': 'claude-sonnet-4-6',
  'claude-3-5-sonnet-20240620': 'claude-sonnet-4-6',
  'claude-3-5-sonnet-latest': 'claude-sonnet-4-6',
  'claude-3-sonnet-20240229': 'claude-sonnet-4-6',
  'claude-3-7-sonnet-20250219': 'claude-sonnet-4-6',
  'claude-sonnet-4-20250514': 'claude-sonnet-4-6',
  'claude-sonnet-4-5-20250929': 'claude-sonnet-4-6',
  'claude-3-haiku-20240307': 'claude-haiku-5-5',
  'claude-3-5-haiku-20241022': 'claude-haiku-5-5',
  'claude-3-5-haiku-latest': 'claude-haiku-5-5',
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
};

/** Only retired IDs listed above are replaced (the UI tells the user); any other saved/custom ID is kept as-is. */
export function migrateModel(id) {
  return (DEPRECATED_MODEL_FALLBACKS[id] || id || DEFAULT_BYOK_MODEL).trim();
}

export const providerOf = (modelId) => BYOK_MODELS.find((m) => m.id === modelId)?.provider || 'anthropic';
export const detectProvider = (key) => (key.startsWith('sk-ant-') ? 'anthropic' : key.startsWith('sk-') ? 'openai' : null);

export const sanitizeKey = (k) => (k || '').replace(/\s+/g, '').replace(/^['"]+|['"]+$/g, '');
export const maskKey = (k) => (k && k.length > 12 ? `${k.slice(0, 7)}…${k.slice(-4)}` : k ? '••••' : '');

const KIND_OF = { 401: 'invalid_key', 403: 'forbidden', 404: 'not_found', 429: 'rate_limit' };
export const KIND_MSG = {
  missing_key: 'Inserisci prima la chiave API', invalid_key: 'Chiave API non valida o revocata', forbidden: 'Permessi insufficienti per questa chiave',
  not_found: 'Risorsa o modello non trovato', rate_limit: 'Troppe richieste o credito esaurito: riprova tra poco', network: 'Rete non raggiungibile o richiesta bloccata dal browser (CORS)',
  timeout: 'Timeout: nessuna risposta entro 8 secondi', invalid_response: 'Risposta del provider non valida', empty: 'Il provider non ha restituito modelli utilizzabili', provider: 'Errore del provider o del server',
};
const HINTS = { 401: 'Chiave API non valida o revocata', 403: 'Chiave senza permessi per questo modello', 404: 'Modello non disponibile per questa chiave', 429: 'Limite di richieste o credito esaurito', 529: 'Servizio temporaneamente sovraccarico' };

const scrub = (t) => (t || '').replace(/sk-[\w*-]{4,}/g, 'sk-…').slice(0, 200);
const MODELS_URL = { anthropic: 'https://api.anthropic.com/v1/models', openai: 'https://api.openai.com/v1/models' };
const authHeaders = (provider, apiKey) => (provider === 'anthropic'
  ? { 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' }
  : { Authorization: `Bearer ${apiKey}` });

/** Lists models straight from the provider. Anthropic is paginated (has_more / last_id); IDs and order are kept as returned. */
export async function discoverProviderModels(provider, apiKey, signal) {
  if (!apiKey) return { ok: false, kind: 'missing_key', error: KIND_MSG.missing_key };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort('timeout'), 8000);
  const onAbort = () => ctrl.abort('cancel');
  signal?.addEventListener('abort', onAbort);
  const models = [];
  try {
    let after = null;
    for (let page = 0; page < 20; page += 1) {
      const qs = provider === 'anthropic' ? `?limit=1000${after ? `&after_id=${encodeURIComponent(after)}` : ''}` : '';
      const res = await fetch(MODELS_URL[provider] + qs, { headers: authHeaders(provider, apiKey), signal: ctrl.signal });
      const j = await res.json().catch(() => null);
      if (!res.ok) {
        const kind = KIND_OF[res.status] || 'provider';
        const msg = j && typeof j.error === 'object' ? j.error?.message : null;
        return { ok: false, kind, status: res.status, error: scrub(`HTTP ${res.status} · ${KIND_MSG[kind]}${msg ? `: ${msg}` : ''}`) };
      }
      if (!j || !Array.isArray(j.data)) return { ok: false, kind: 'invalid_response', error: KIND_MSG.invalid_response };
      j.data.forEach((m) => { if (m && typeof m.id === 'string') models.push({ id: m.id, display_name: m.display_name || null, created: m.created_at || m.created || null }); });
      if (provider !== 'anthropic' || !j.has_more || !j.last_id) break;
      after = j.last_id;
    }
  } catch (e) {
    if (ctrl.signal.aborted) return ctrl.signal.reason === 'cancel' ? { ok: false, kind: 'cancelled' } : { ok: false, kind: 'timeout', error: KIND_MSG.timeout };
    return { ok: false, kind: 'network', blocked: true, error: KIND_MSG.network };
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', onAbort); }
  if (!models.length) return { ok: false, kind: 'empty', error: KIND_MSG.empty };
  return { ok: true, models };
}

const CACHE = 'nexora.byokModels';
/** Non-secret cache: provider, model IDs/names and sync time only. Never the key. */
export const modelCache = {
  get: (provider) => { try { const c = JSON.parse(localStorage.getItem(CACHE)); return c && c.provider === provider ? c : null; } catch { return null; } },
  set: (provider, models) => localStorage.setItem(CACHE, JSON.stringify({ provider, models: models.map(({ id, display_name }) => ({ id, display_name })), syncedAt: new Date().toISOString() })),
  clear: () => localStorage.removeItem(CACHE),
};

const LIMIT_HEADERS = {
  anthropic: { requests: ['anthropic-ratelimit-requests-limit', 'anthropic-ratelimit-requests-remaining'], tokens: ['anthropic-ratelimit-tokens-limit', 'anthropic-ratelimit-tokens-remaining'] },
  openai: { requests: ['x-ratelimit-limit-requests', 'x-ratelimit-remaining-requests'], tokens: ['x-ratelimit-limit-tokens', 'x-ratelimit-remaining-tokens'] },
};
/** Same rules as the server: low when ≤20% left; credit vs rate exhaustion from the error body. Headers may be hidden by CORS. */
export function keyStatusFromHeaders(provider, headers, status, body) {
  const limits = {}; const low = [];
  Object.entries(LIMIT_HEADERS[provider] || {}).forEach(([k, [lh, rh]]) => {
    const lim = parseInt(headers.get(lh), 10); const rem = parseInt(headers.get(rh), 10);
    if (lim > 0 && !Number.isNaN(rem)) { limits[k] = { limit: lim, remaining: rem }; if (rem / lim <= 0.2) low.push(k); }
  });
  const e = body && typeof body.error === 'object' ? body.error : {};
  const t = `${e.code || ''} ${e.type || ''} ${e.message || ''}`.toLowerCase();
  const exhausted = status === 200 ? null : /insufficient_quota|credit balance|billing/.test(t) ? 'credit' : status === 429 || /rate_limit/.test(t) ? 'rate' : null;
  if (!Object.keys(limits).length && !exhausted) return null;
  return { provider, checked_at: new Date().toISOString(), limits, low, exhausted, threshold: 0.2 };
}

/** Pings the official provider endpoint straight from this device; the key never touches our server. */
export async function testBYOKConnection(provider, apiKey, modelId) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 8000);
  const t0 = performance.now();
  const body = JSON.stringify({ model: modelId, max_tokens: 1, messages: [{ role: 'user', content: 'ping' }] });
  try {
    const res = provider === 'anthropic'
      ? await fetch('https://api.anthropic.com/v1/messages', { method: 'POST', signal: ctrl.signal, body, headers: { 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'content-type': 'application/json', 'anthropic-dangerous-direct-browser-access': 'true' } })
      : await fetch('https://api.openai.com/v1/chat/completions', { method: 'POST', signal: ctrl.signal, body, headers: { Authorization: `Bearer ${apiKey}`, 'content-type': 'application/json' } });
    const latency = Math.round(performance.now() - t0);
    const j = await res.json().catch(() => ({}));
    const ks = keyStatusFromHeaders(provider, res.headers, res.status, j);
    if (!res.ok) {
      const msg = typeof j.error === 'object' ? j.error?.message : j.error;
      return { ok: false, status: res.status, latency_ms: latency, key_status: ks, error: `HTTP ${res.status} · ${HINTS[res.status] || 'Errore del provider'}${msg ? `: ${msg}` : ''}`.replace(/sk-[\w*-]{4,}/g, 'sk-…') };
    }
    return { ok: true, latency_ms: latency, model: j.model || modelId, key_status: ks };
  } catch (e) {
    if (e.name === 'AbortError') return { ok: false, latency_ms: Math.round(performance.now() - t0), error: 'Timeout: nessuna risposta entro 8 secondi' };
    return { ok: false, blocked: true, latency_ms: Math.round(performance.now() - t0), error: 'Connessione fallita: controlla la rete' };
  } finally { clearTimeout(timer); }
}
